import { createApp } from 'vue'
// @ts-ignore
import App from './App.vue'
import router from './router/router'
import i18n from '@/i18n'
import store from '@/store'
import globalPlugins from '@/plugins/globalPlugins'
import { sync } from 'vuex-router-sync'
import pwa from '@/plugins/pwa'
import '@/assets/css/index.scss'
// @ts-ignore
import 'virtual:svg-icons-register'
import './polyfills'
import * as Sentry from '@sentry/vue'
import { API_URL, CLIENT_RELEASE, DEPLOY_CONTEXT, SENTRY_BASE_URL } from '@/modules/common/config'
import { registerAuthorizedUserGuard } from '@/router/authorizedUserGuard'
import { registerPageViewLog } from '@/router/pageViewLog'
import { registerLinkUsageLog } from '@/modules/common/utils/linkUsageLog'
import { registerStaleDeployReload } from '@/router/staleDeployReload'
import { STALE_DEPLOY_ERROR_PATTERNS } from '@/modules/common/utils/staleDeployErrors'
import { THIRD_PARTY_SCRIPT_URL_PATTERNS } from '@/modules/common/utils/thirdPartyScriptErrors'
import {
	dropExpiredSessionErrors,
	DROPPED_EVENT_ERROR_PATTERN,
} from '@/modules/common/utils/expiredSessionErrors'
import { BROWSER_INJECTED_ERROR_PATTERNS } from '@/modules/common/utils/browserInjectedErrors'
import { redactBreadcrumb, redactEvent } from '@/modules/common/utils/sentryRedaction'

sync(store, router)

// Registered here rather than in router.ts so the router doesn't have to import the store,
// which would close an import cycle through apiConfig.
registerAuthorizedUserGuard(router, store)
registerPageViewLog(router, store)
registerStaleDeployReload(router)
registerLinkUsageLog()

const app = createApp(App).use(store).use(i18n).use(router).use(globalPlugins).use(pwa)

app.use(
	createAuth0({
		domain: AUTH0_DOMAIN,
		clientId: AUTH0_CLIENT_ID,
		cookieDomain: '.givelegacy.com',
		cacheLocation: 'localstorage',
		authorizationParams: {
			redirect_uri: AUTH0_CALLBACK_URL,
			audience: 'https://dashboard.givelegacy.com/',
		},
	}),
)

Sentry.init({
	app,
	dsn: SENTRY_BASE_URL,
	release: CLIENT_RELEASE ?? undefined,
	environment: DEPLOY_CONTEXT ?? 'local',
	tracePropagationTargets: ['localhost', API_URL],
	integrations: [
		new Sentry.BrowserTracing({
			routingInstrumentation: Sentry.vueRouterInstrumentation(router),
		}),
	],
	ignoreErrors: [
		// The router reloads the page on these, so they are recovered from rather than reported.
		...STALE_DEPLOY_ERROR_PATTERNS,
		...BROWSER_INJECTED_ERROR_PATTERNS,
		DROPPED_EVENT_ERROR_PATTERN,
	],
	denyUrls: THIRD_PARTY_SCRIPT_URL_PATTERNS,
	beforeSend: (event, hint) => {
		const keptEvent = dropExpiredSessionErrors(event, hint)

		return keptEvent === null ? null : redactEvent(keptEvent)
	},
	beforeSendTransaction: redactEvent,
	beforeBreadcrumb: redactBreadcrumb,
	tracesSampleRate: 0.1,
})

app.mount('#app')

import './modules/common/apiConfig'
import { createAuth0 } from '@auth0/auth0-vue'
import { AUTH0_DOMAIN, AUTH0_CLIENT_ID, AUTH0_CALLBACK_URL } from '@/modules/common/config'
